import {
    buildDidYouMean,
    buildFacet,
    buildInteractiveResult,
    buildQuerySummary,
    buildResultList,
    buildResultsPerPage,
    buildSearchBox,
    buildSearchParameterManager,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
} from '@coveo/headless';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import React, { useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';
import { preserveHashInUrl } from '@/utils/preserveHashInUrl';
import { summarizeArray } from '@/utils/summarizeArray';

const SEARCH_PARAM_KEY = 'q';
const FILTER_PARAM_KEY = 'f';
const PAGE_SIZE = 20;
const CARD_HEADER_HEIGHT = 90;

// Default Coveo search state
const defaultResultsState = {
    firstSearchExecuted: false,
    hasError: false,
    hasResults: false,
    isLoading: false,
    moreResultsAvailable: false,
    results: [],
    searchResponseId: '',
};

// Default Filters state
const defaultFilterState = {
    Provider: {
        state: 'idle',
        value: 'Provider',
        numberOfResults: 0,
    },
    Location: {
        state: 'idle',
        value: 'Location',
        numberOfResults: 0,
    },
    News: {
        state: 'idle',
        value: 'News',
        numberOfResults: 0,
    },
    AreasOfCare: {
        state: 'idle',
        value: 'AreasOfCare',
        numberOfResults: 0,
    },
    Trial: {
        state: 'idle',
        value: 'Trial',
        numberOfResults: 0,
    },
    Article: {
        state: 'idle',
        value: 'Article',
        numberOfResults: 0,
    },
};

// Default Coveo Facets state
const defaultFacetsState = {
    values: [],
    isLoading: false,
    facetId: 'filetype',
    enabled: true,
};

// Default Did You Mean state
const defaultDidYouMeanState = {
    hasQueryCorrection: false,
    wasAutomaticallyCorrected: false,
    queryCorrection: {
        correctedQuery: '',
    },
};

// Default SiteSearch context state
const defaultValue = {
    coveo: {
        engine: null,
        controller: {
            searchBox: null,
            querySummary: null,
            resultList: null,
        },
        action: {
            updateQuery: null,
            executeSearch: null,
        },
    },
    CARD_HEADER_HEIGHT,
    filters: defaultFilterState,
    filterValue: '',
    handleSubmit: () => {},
    PAGE_SIZE,
    recommendedResults: [],
    results: [],
    searchValue: '',
    totalCount: 0,
    triggerExecuteSearch: () => {},
};

const SiteSearchContext = React.createContext(defaultValue);

/**
 * Check if it's an internal link
 * @param {string} type - The result type
 * @param {string} url - The result URL
 * @returns {boolean} The isInternal value
 */
const getIsInternal = (type, url) => {
    if (type === 'location' || type === 'provider') return true;

    const internalDomains = ['atlantichealth.org'];

    const linkUrl = new URL(url);
    const domain = linkUrl.hostname;

    const isInternal = internalDomains.some(
        (internalDomain) => domain === internalDomain || domain.endsWith(`.${internalDomain}`),
    );

    return isInternal;
};

/**
 * Override specific coveo labels with custom labels
 * @param {string} value - The original label
 * @returns {string} - The updated label
 */
const getLabelOverride = (value) => {
    switch (value) {
        case 'Provider':
            return 'Medical Team';
        case 'Trial':
            return 'Clinical Trial';
        case 'html':
            return '';
        case 'YouTubeVideo':
            return '';
        default:
            return value.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
};

/**
 * Convert search params from coveo to URLSearchParams
 * @param {object} params - The Coveo search params
 * @returns {string} - The serialized params
 */
const serializeParams = (params) => {
    const serializedParams = new URLSearchParams(window.location.search);
    const { q, f } = params;
    const a = serializedParams.get('a');

    serializedParams.delete('q');
    serializedParams.delete('f');

    if (q) serializedParams.set('q', q);
    if (f) serializedParams.set('f', f.filetype?.[0]);

    // Remove the query correction parameter if a new query is entered
    if (a !== q) serializedParams.delete('a');

    return serializedParams.toString();
};

/**
 * Convert URLSearchParams to coveo search params
 * @param {string} params - The URL search params
 * @returns {object} - The deserialized params
 */
const deserializeParams = (params) => {
    const updatedSearchParams = new URLSearchParams(params);
    const q = updatedSearchParams.get('q');
    const f = updatedSearchParams.get('f');
    return { ...(q ? { q, f: { filetype: f ? [f] : [] } } : {}) };
};

/**
 * SiteSearch context provider
 * @param {object} props - The component props
 */
export const SiteSearchProvider = (props) => {
    const history = useHistory();
    const location = useLocation();

    const searchParams = useSearchParams();
    const deserializedSearchParams = React.useMemo(
        () => deserializeParams(searchParams),
        [searchParams],
    );
    const filterValue = searchParams.get(FILTER_PARAM_KEY);
    const searchValue = searchParams.get(SEARCH_PARAM_KEY);

    const [filters, setFilters] = React.useState(defaultFilterState);
    const [activeFilter, setActiveFilter] = React.useState(null);
    const [facetsState, setFacetsState] = React.useState(defaultFacetsState);
    const [resultsState, setResultsState] = React.useState(defaultResultsState);
    const [totalCount, setTotalCount] = React.useState(0);
    const [results, setResults] = React.useState([]);
    const [recommendedResults, setRecommendedResults] = React.useState([]);
    const [didYouMean, setDidYouMean] = React.useState(defaultDidYouMeanState);
    const [isNewResults, setIsNewResults] = React.useState(false);
    const prevSearchResponseId = React.useRef('');

    const { children, id, coveo, fixedRecommendedCards, enableRecommendedResults } = props;

    // Coveo search engine
    const engine = React.useMemo(
        () =>
            getHeadlessEngine(`site-search-${id}`, {
                ...coveo,
                analytics: { analyticsMode: 'legacy', trackingId: 'global-search' },
            }),
        [coveo, id],
    );
    const QueryActionCreators = React.useMemo(() => loadQueryActions(engine), [engine]);
    const SearchAnalyticsActionCreators = React.useMemo(
        () => loadSearchAnalyticsActions(engine),
        [engine],
    );
    const SearchActionCreators = React.useMemo(() => loadSearchActions(engine), [engine]);
    const controllerSearchBox = React.useMemo(
        () =>
            buildSearchBox(engine, {
                options: { id: 'site-search-searchbox', numberOfSuggestions: 10 },
            }),
        [engine],
    );
    const controllerQuerySummary = React.useMemo(() => buildQuerySummary(engine), [engine]);
    const controllerResultsList = React.useMemo(
        () =>
            buildResultList(engine, {
                options: {
                    fieldsToInclude: [
                        'locname',
                        'filetype',
                        'locparent',
                        'locroot',
                        'locbuildingname',
                        'locaddrline1',
                        'locaddrline2',
                        'locaddrline3',
                        'locationstate',
                        'imageurl',
                        'locationzip',
                        'locmapurl',
                        'locaddressoverridemessage',
                        'locphnum1',
                        'primaryspecialty',
                        'specialties',
                        'trialsstatus',
                        'ctpagedescription',
                        'acceptingnewpatients',
                        'onlinebookingavailable',
                        'description',
                    ],
                },
            }),
        [engine],
    );

    const resultsPerPageController = buildResultsPerPage(engine, {
        initialState: { numberOfResults: PAGE_SIZE },
    });

    const controllerFacets = React.useMemo(() => {
        const facetOptions = {
            options: {
                field: 'filetype',
                sortCriteria: 'occurrences',
                injectionDepth: 1000,
            },
        };

        // Check if the facet is already registered to prevent duplication
        if (!engine.state?.facetSet?.[facetOptions.options.field]) {
            return buildFacet(engine, facetOptions);
        }
        return engine.state.facetSet[facetOptions.options.field];
    }, [engine]);
    const controllerUrlParams = React.useMemo(
        () =>
            buildSearchParameterManager(engine, {
                initialState: {
                    parameters: deserializedSearchParams,
                },
                options: {
                    urlManager: {
                        useHash: false,
                    },
                },
            }),
        [engine, deserializedSearchParams],
    );
    const controllerDidYouMean = React.useMemo(
        () =>
            buildDidYouMean(engine, {
                options: {
                    automaticallyCorrectQuery: false,
                },
            }),
        [engine],
    );

    const coveoAnalyticsRef = useRef({ event: '', payload: {}, shouldReset: true });

    /**
     * Apply suggested query correction
     * @returns {Promise<void>}
     */
    const applyCorrection = React.useCallback(() => {
        return controllerDidYouMean.applyCorrection();
    }, [controllerDidYouMean]);

    /**
     * Execute coveo search
     * @returns {Promise<void>}
     */
    const executeSearch = React.useCallback(
        (event, payload = {}) => {
            const {
                logInterfaceLoad,
                logSearchboxSubmit,
                logResultsSort,
                logQuerySuggestionClick,
                logFacetSelect,
                logFacetDeselect,
                logFacetBreadcrumb,
                logClearBreadcrumbs,
                logStaticFilterSelect,
                logStaticFilterDeselect,
                logStaticFilterClearAll,
            } = SearchAnalyticsActionCreators;
            switch (event) {
                case 'resultsSort':
                    return SearchActionCreators.executeSearch(logResultsSort());
                case 'searchboxSubmit':
                    return SearchActionCreators.executeSearch(logSearchboxSubmit());
                case 'querySuggestionClick':
                    return SearchActionCreators.executeSearch(logQuerySuggestionClick(payload)); // LogQuerySuggestionClickActionCreatorPayload {id: string, suggestion: string}
                case 'facetSelect':
                    return SearchActionCreators.executeSearch(logFacetSelect(payload)); // LogFacetSelectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetDeselect':
                    return SearchActionCreators.executeSearch(logFacetDeselect(payload)); // LogFacetDeselectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetBreadcrumb':
                    return SearchActionCreators.executeSearch(logFacetBreadcrumb(payload)); // LogFacetBreadcrumbActionCreatorPayload {facetId: string, facetValue: string}
                case 'clearBreadcrumbs':
                    return SearchActionCreators.executeSearch(logClearBreadcrumbs());
                case 'staticFilterSelect':
                    return SearchActionCreators.executeSearch(logStaticFilterSelect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterDeselect':
                    return SearchActionCreators.executeSearch(logStaticFilterDeselect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterClearAll':
                    return SearchActionCreators.executeSearch(logStaticFilterClearAll(payload)); // LogStaticFilterClearAllActionCreatorPayload {staticFilterId: string}
                default:
                    return SearchActionCreators.executeSearch(logInterfaceLoad());
            }
        },
        [SearchActionCreators, SearchAnalyticsActionCreators],
    );

    /**
     * Trigger execute search function
     * @param {string} searchValue - The search value
     * @returns {void}
     */
    const triggerExecuteSearch = React.useCallback(
        ({ searchValue, analyticsActionCreator }) => {
            if (searchValue) {
                engine.dispatch(
                    QueryActionCreators.updateQuery({
                        q: searchValue,
                    }),
                );
                if (analyticsActionCreator) {
                    setCoveoAnalytics(
                        analyticsActionCreator.event,
                        analyticsActionCreator.payload,
                        false,
                    );
                }
                engine.dispatch(
                    executeSearch(
                        coveoAnalyticsRef?.current?.event,
                        coveoAnalyticsRef?.current?.payload,
                    ),
                );
            }
        },
        [engine, QueryActionCreators, executeSearch],
    );

    /**
     * Filter search results by type
     * @param {string} type - The filter type that will restrict the search
     * @returns {void}
     */
    const triggerExecuteFilter = React.useCallback(
        ({ filterValue }) => {
            controllerFacets.deselectAll();
            let facetValue = controllerFacets?.state?.values.find((v) => v.value === filterValue);
            controllerFacets.toggleSingleSelect(facetValue);
            setCoveoAnalytics(
                controllerFacets.isValueSelected(facetValue) ? 'facetDeselect' : 'facetSelect',
                {
                    facetId: 'filetype',
                    facetValue: filterValue,
                },
            );
        },
        [controllerFacets],
    );

    /**
     * Clear search filters
     * @returns {void}
     */
    const clearSearchFilters = React.useCallback(() => {
        try {
            controllerFacets.deselectAll();
        } catch {
            console.error('Error clearing search filters');
        }
    }, [controllerFacets]);

    // Track search results for analytics
    const trackResults = React.useCallback(() => {
        const filterValues = [];
        let filterTags = '';
        // Concatenate the recommended card titles to Title1 | Title2
        const recommendedCardTitles = Array.isArray(recommendedResults)
            ? recommendedResults.map((result) => result?.title).join(' | ')
            : '';

        // Concatenate filter name and number of results to Provider 10 | Location 5
        Object.values(filters).forEach((filter) => {
            if (filter?.value) {
                filterValues.push(`${getLabelOverride(filter.value)} ${filter.numberOfResults}`);
            }
        });

        if (filterValues.length > 0) {
            filterTags = filterValues.join(' | ');
        }

        trackInteraction({
            eventName: 'search_results',
            searchTerm: searchValue,
            searchFilters: filterTags,
            searchRecommendedCard: recommendedCardTitles,
            searchResultCount: totalCount,
        });
    }, [searchValue, totalCount, filters, recommendedResults]);

    React.useEffect(() => {
        if (controllerFacets && controllerFacets.subscribe) {
            /**
             * Subscribe to the facet state for filtering search results
             * @returns {function} The unsubscribe function
             */
            const unsubscribe = controllerFacets.subscribe(() => {
                const { values } = controllerFacets.state;
                const newFilters = { ...defaultFilterState };
                Object.keys(newFilters).forEach((key) => {
                    newFilters[key] = values.find((item) => item.value === key) || newFilters[key];
                });

                setFilters(newFilters);
                setActiveFilter(
                    values.find((item) => item.state === 'selected')?.value?.toLowerCase(),
                );
                setFacetsState(controllerFacets.state);
            });

            return () => {
                // Unsubscribe from the facet state
                unsubscribe();
            };
        }
    }, [controllerFacets]);

    React.useEffect(() => {
        /**
         * Subscribe to the did you mean state
         * @returns {function} The unsubscribe function
         */
        const unsubscribe = controllerDidYouMean.subscribe(() => {
            const {
                hasQueryCorrection,
                queryCorrection: { correctedQuery },
            } = controllerDidYouMean.state;
            const { total } = controllerQuerySummary.state;

            // If there is a query correction and no results, update the URL and apply the correction
            if (hasQueryCorrection && total === 0) {
                const updatedParams = new URLSearchParams(location.search);
                updatedParams.set('a', correctedQuery);
                // AHSMKTG-4189: preserve any existing hash (e.g. tab deep-link) when the
                // query-correction rewrite replaces the URL.
                history.replace(
                    preserveHashInUrl({
                        pathname: location.pathname,
                        search: updatedParams.toString(),
                    }),
                );
                applyCorrection();
            }
            setDidYouMean(controllerDidYouMean.state);
        });

        return () => {
            // Unsubscribe from the did you mean state
            unsubscribe();
        };
    }, [
        controllerDidYouMean,
        controllerQuerySummary,
        history,
        location.search,
        location.pathname,
        applyCorrection,
    ]);

    React.useEffect(() => {
        /**
         * Subscribe to the results list
         * @returns {function} The unsubscribe function
         */
        const unsubscribe = controllerResultsList.subscribe(() => {
            // Parse results to match the SearchResultsList props
            const parsedResults = controllerResultsList.state.results
                .map((result) => {
                    const { title, clickUri, excerpt, isRecommendation, raw } = result;
                    const {
                        locname,
                        filetype,
                        imageurl,
                        locmapurl,
                        locaddressoverridemessage,
                        locbuildingname,
                        locationstate,
                        locationzip,
                        locparent,
                        locroot,
                        locaddrline1,
                        locaddrline2,
                        locaddrline3,
                        primaryspecialty,
                        specialties: rawSpecialties,
                        trialsstatus,
                        ctpagedescription,
                        acceptingnewpatients,
                        onlinebookingavailable,
                        description,
                    } = raw;

                    // Specialties to ignore
                    const ignoreSpecialties = ['Primary Care'];
                    // Filter out ignored specialties
                    const specialties = Array.isArray(rawSpecialties)
                        ? rawSpecialties.filter((specItem) => !ignoreSpecialties.includes(specItem))
                        : [];
                    const interactiveResultController = engine
                        ? buildInteractiveResult(engine, {
                              options: { result: result },
                          })
                        : null;
                    const type = filetype.toLowerCase();
                    const parsedDescription =
                        type === 'provider' && (specialties?.length > 0 || primaryspecialty)
                            ? summarizeArray(specialties, 3, primaryspecialty)
                            : type === 'areasofcare'
                              ? ctpagedescription
                              : excerpt || description;

                    const resultObject = {
                        badge: {
                            type,
                            label: getLabelOverride(filetype),
                        },
                        title: locname || title,
                        description: parsedDescription,
                        type,
                        isInternal: getIsInternal(type, clickUri),
                        link: {
                            href: clickUri,
                            csr: false,
                            ariaLabel: `View details for ${locname || title}`,
                        },
                        ...(isRecommendation ? { isRecommendation } : {}),
                        ...(type === 'provider' &&
                            imageurl && {
                                image: {
                                    src: imageurl,
                                    alt: `Headshot of ${locname || title}`,
                                },
                            }),
                        ...(type === 'provider' &&
                            acceptingnewpatients === 'True' && {
                                status: {
                                    isActive: true,
                                    label: 'Accepting new patients',
                                },
                            }),
                        ...(type === 'provider' &&
                            onlinebookingavailable === 'True' && {
                                appointmentsCta: {
                                    label: 'Appointments',
                                    ariaLabel: `Schedule an appointment with ${title}`,
                                    href: `${new URL(clickUri).pathname}#scheduling`,
                                    target: '_self',
                                    csr: true,
                                },
                            }),
                        ...(type === 'location' && {
                            ...(!locaddressoverridemessage?.trim() && {
                                directionsCta: {
                                    href: locmapurl,
                                    ariaLabel: `Get directions to ${locname || title}`,
                                },
                            }),
                            location: {
                                rootParent: locroot,
                                parent: locparent,
                                building: locbuildingname,
                                streetOne: locaddrline1,
                                streetTwo: locaddrline2,
                                city: locaddrline3?.split(',')[0],
                                state: locationstate?.join(),
                                zip: locationzip?.join(),
                                addressOverrideMessage: locaddressoverridemessage,
                            },
                        }),
                        ...(type === 'trial' &&
                            trialsstatus && {
                                status: {
                                    label: trialsstatus,
                                    isActive: trialsstatus.toLowerCase() === 'recruiting',
                                },
                            }),
                        coveoAnalyticsCallbacks: interactiveResultController
                            ? {
                                  cancelPendingSelect: () =>
                                      interactiveResultController.cancelPendingSelect(),
                                  onClick: () => interactiveResultController.select(),
                                  onContextMenu: () => interactiveResultController.select(),
                                  onTouchStart: () =>
                                      interactiveResultController.beginDelayedSelect(),
                                  onTouchEnd: () =>
                                      interactiveResultController.cancelPendingSelect(),
                              }
                            : undefined,
                    };

                    return resultObject;
                })
                .filter((result) => result !== null);

            let newResults = parsedResults;
            const { total } = controllerQuerySummary.state;

            if (enableRecommendedResults) {
                const recommendedResults = parsedResults.filter(
                    (result) => result.isRecommendation,
                );
                newResults = parsedResults.filter((result) => !result.isRecommendation);
                setRecommendedResults(recommendedResults);
            }

            setTotalCount(total);
            setResults(newResults);
            setResultsState(controllerResultsList.state);

            // Check if the search results are new via the search response id
            setIsNewResults(
                controllerResultsList.state.searchResponseId &&
                    prevSearchResponseId.current?.indexOf(
                        controllerResultsList.state.searchResponseId,
                    ) !== 0,
            );

            // store the previous results state to compare with the next results state
            prevSearchResponseId.current = controllerResultsList.state.searchResponseId;
        });

        return () => {
            // Unsubscribe from the results list
            unsubscribe();
        };
    }, [
        controllerResultsList,
        controllerQuerySummary,
        enableRecommendedResults,
        trackResults,
        engine,
    ]);

    // If there are new results, track the search results
    React.useEffect(() => {
        if (isNewResults) {
            trackResults();
        }
    }, [isNewResults, trackResults]);

    React.useEffect(() => {
        /**
         * Subscribe to the search parameter manager
         * @returns {function} The unsubscribe function
         */
        const unsubscribe = controllerUrlParams.subscribe(() => {
            const serializedParams = serializeParams(controllerUrlParams.state.parameters);

            const locationParams = new URLSearchParams(location.search);
            const locationQ = locationParams.get('q');
            const locationF = locationParams.get('f');

            const coveoParams = new URLSearchParams(serializedParams);
            const coveoQ = coveoParams.get('q');
            const coveoF = coveoParams.get('f');

            if (locationQ !== coveoQ || locationF !== coveoF) {
                // AHSMKTG-4189: preserve any existing hash (e.g. tab deep-link) when Coveo's
                // url-manager syncs its state back into the URL.
                history.push(
                    preserveHashInUrl({
                        pathname: location.pathname,
                        search: serializedParams,
                    }),
                );
            }
        });

        return () => {
            unsubscribe();
        };
    }, [controllerUrlParams, history, location.pathname, location.search]);

    React.useEffect(() => {
        // Synchronize the search parameters with the URL
        if (deserializedSearchParams?.f?.filetype?.length) {
            controllerUrlParams.synchronize(deserializedSearchParams);
        } else {
            triggerExecuteSearch({ searchValue: deserializedSearchParams?.q });
        }
    }, [deserializedSearchParams, controllerUrlParams, triggerExecuteSearch]);

    const setCoveoAnalytics = (event = '', payload = {}, shouldReset = true) => {
        coveoAnalyticsRef.current.event = event;
        coveoAnalyticsRef.current.payload = payload;
        coveoAnalyticsRef.current.shouldReset = shouldReset;
    };
    // SiteSearch context state
    const value = React.useMemo(
        () => ({
            activeFilter,
            applyCorrection,
            CARD_HEADER_HEIGHT,
            clearSearchFilters,
            coveo: {
                engine,
                controller: {
                    searchBox: controllerSearchBox,
                    querySummary: controllerQuerySummary,
                    resultList: controllerResultsList,
                    resultsPerPage: resultsPerPageController,
                },
                action: {
                    updateQuery: QueryActionCreators.updateQuery,
                    executeSearch,
                },
            },
            didYouMean,
            facetsState,
            filters,
            filterValue,
            fixedRecommendedCards,
            getLabelOverride,
            recommendedResults,
            results,
            resultsState,
            searchValue,
            totalCount,
            triggerExecuteFilter,
            triggerExecuteSearch,
            setCoveoAnalytics,
        }),
        [
            activeFilter,
            applyCorrection,
            clearSearchFilters,
            controllerQuerySummary,
            controllerResultsList,
            resultsPerPageController,
            controllerSearchBox,
            didYouMean,
            engine,
            executeSearch,
            facetsState,
            filters,
            filterValue,
            fixedRecommendedCards,
            QueryActionCreators.updateQuery,
            recommendedResults,
            results,
            resultsState,
            searchValue,
            totalCount,
            triggerExecuteFilter,
            triggerExecuteSearch,
        ],
    );

    return <SiteSearchContext.Provider value={value}>{children}</SiteSearchContext.Provider>;
};

export const useSiteSearchContext = () => {
    const context = React.useContext(SiteSearchContext);

    if (!context) {
        throw new Error(`'useSiteSearchContext' must be used within a SiteSearchProvider`);
    }

    return context;
};
